import { useQuery } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import type {
  AllergyResponse,
  ImmunizationResponse,
  LabResultResponse,
  MedicationResponse,
  NoteResponse,
  ProblemResponse,
  VitalResponse,
} from '@emr/shared';

import {
  fetchMyChartRecord,
  listMyAllergies,
  listMyImmunizations,
  listMyLabResults,
  listMyMedications,
  listMyProblems,
  listMySignedNotes,
  listMyVitals,
} from '../../api/patientPortal/chart.ts';
import { AsyncSection } from '../../components/AsyncState.tsx';
import { Badge, DetailRow, Panel } from '../../components/primitives.tsx';
import { useDateTimeFormat } from '../../format/dateTimeFormatContext.ts';
import { formatDateAndTime, formatPreferredCalendarDate } from '../../format/formatDateTime.ts';
import { PortalHelpLink } from '../help/PortalHelpLink.tsx';

/**
 * "My records" — the patient portal's records-access surface (`BACKLOG.md` W2 pillar 1,
 * `docs/decisions/patient-portal-scope.md`'s "Records access" section). Every list returned by
 * `api/patientPortal/chart.ts` is already the caller's *complete* record with no confidentiality-
 * label filtering (that module's own header), so this page renders every row it gets with nothing
 * held back — there is no "show more" gate to build here the way a staff chart view would need one.
 *
 * One tab fetched at a time (`enabled: activeTab === ...`), not all seven queries fired on mount —
 * the patient portal is disproportionately likely to be used on a phone connection, and a first
 * paint that only needs the demographics header plus whichever tab is showing is real, cheap
 * savings over the staff `PatientChartPage`'s own "fetch every tab up front" shape (which can afford
 * that because a clinician's chart view usually gets used tab-to-tab in the same visit anyway).
 */
export function PortalRecordsPage() {
  const { t } = useTranslation('portalRecords');
  const { preference } = useDateTimeFormat();
  const [activeTab, setActiveTab] = useState<TabKey>('problems');

  const patientQuery = useQuery({
    queryKey: ['portal', 'chart', 'patient'],
    queryFn: ({ signal }: { signal: AbortSignal }) => fetchMyChartRecord(signal),
  });

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center gap-2">
        <h1 className="m-0 text-xl font-semibold text-ink">{t('heading')}</h1>
        <PortalHelpLink topicId="your-medical-records" />
      </div>

      <AsyncSection query={patientQuery} loadingLabel={t('demographics.loading')}>
        {(patient) => (
          <Panel title={t('demographics.panelTitle')}>
            <dl className="m-0">
              <DetailRow label={t('demographics.name')}>
                {patient.firstName} {patient.lastName}
              </DetailRow>
              <DetailRow label={t('demographics.dateOfBirth')}>
                {formatPreferredCalendarDate(patient.dateOfBirth, preference)}
              </DetailRow>
              <DetailRow label={t('demographics.mrn')}>{patient.mrn}</DetailRow>
              <DetailRow label={t('demographics.phone')}>{patient.phone ?? '—'}</DetailRow>
              <DetailRow label={t('demographics.email')}>{patient.email ?? '—'}</DetailRow>
            </dl>
          </Panel>
        )}
      </AsyncSection>

      <div role="tablist" aria-label={t('tabsLabel')} className="flex flex-wrap gap-1">
        {TAB_KEYS.map((key) => (
          <button
            key={key}
            type="button"
            role="tab"
            aria-selected={activeTab === key}
            onClick={() => {
              setActiveTab(key);
            }}
            className={[
              'rounded-control px-3 py-2 text-sm font-medium',
              activeTab === key ? 'bg-nav-active text-nav-active-ink' : 'text-ink hover:bg-raised',
            ].join(' ')}
          >
            {t(`tabs.${key}`)}
          </button>
        ))}
      </div>

      <div role="tabpanel">
        {activeTab === 'problems' ? <ProblemsPanel /> : null}
        {activeTab === 'allergies' ? <AllergiesPanel /> : null}
        {activeTab === 'medications' ? <MedicationsPanel /> : null}
        {activeTab === 'immunizations' ? <ImmunizationsPanel /> : null}
        {activeTab === 'vitals' ? <VitalsPanel /> : null}
        {activeTab === 'labs' ? <LabsPanel /> : null}
        {activeTab === 'notes' ? <NotesPanel /> : null}
      </div>
    </div>
  );
}

const TAB_KEYS = [
  'problems',
  'allergies',
  'medications',
  'immunizations',
  'vitals',
  'labs',
  'notes',
] as const;
type TabKey = (typeof TAB_KEYS)[number];

/** One record's worth of label/value rows — every tab below renders a list of these instead of a
 * table, matching `claude-kit/rules/invariants.md`'s "table columns need a deliberate strategy for
 * long content": a stacked card has no column to overflow in the first place, and `DetailRow`
 * already wraps long values (`primitives.tsx`'s own `break-words [overflow-wrap:anywhere]`). */
function RecordCard({ children }: { readonly children: ReactNode }) {
  return (
    <div className="rounded-control border border-line bg-surface p-3">
      <dl className="m-0">{children}</dl>
    </div>
  );
}

function ProblemsPanel() {
  const { t } = useTranslation('portalRecords');
  const { preference } = useDateTimeFormat();
  const query = useQuery({
    queryKey: ['portal', 'chart', 'problems'],
    queryFn: ({ signal }: { signal: AbortSignal }) => listMyProblems(signal),
  });
  return (
    <AsyncSection<readonly ProblemResponse[]>
      query={query}
      loadingLabel={t('problems.loading')}
      isEmpty={(data) => data.length === 0}
      emptyLabel={t('problems.empty')}
    >
      {(problems) => (
        <div className="flex flex-col gap-2">
          {problems.map((problem) => (
            <RecordCard key={problem.id}>
              <DetailRow label={t('problems.description')}>{problem.description}</DetailRow>
              <DetailRow label={t('problems.status')}>
                <Badge>{problem.status}</Badge>
              </DetailRow>
              <DetailRow label={t('problems.onset')}>
                {problem.onsetDate === null
                  ? '—'
                  : formatPreferredCalendarDate(problem.onsetDate, preference)}
              </DetailRow>
            </RecordCard>
          ))}
        </div>
      )}
    </AsyncSection>
  );
}

function AllergiesPanel() {
  const { t } = useTranslation('portalRecords');
  const query = useQuery({
    queryKey: ['portal', 'chart', 'allergies'],
    queryFn: ({ signal }: { signal: AbortSignal }) => listMyAllergies(signal),
  });
  return (
    <AsyncSection<readonly AllergyResponse[]>
      query={query}
      loadingLabel={t('allergies.loading')}
      isEmpty={(data) => data.length === 0}
      emptyLabel={t('allergies.empty')}
    >
      {(allergies) => (
        <div className="flex flex-col gap-2">
          {allergies.map((allergy) => (
            <RecordCard key={allergy.id}>
              <DetailRow label={t('allergies.substance')}>{allergy.substance}</DetailRow>
              <DetailRow label={t('allergies.reaction')}>{allergy.reaction ?? '—'}</DetailRow>
              <DetailRow label={t('allergies.severity')}>{allergy.severity ?? '—'}</DetailRow>
              <DetailRow label={t('allergies.status')}>
                <Badge>{allergy.status}</Badge>
              </DetailRow>
            </RecordCard>
          ))}
        </div>
      )}
    </AsyncSection>
  );
}

function MedicationsPanel() {
  const { t } = useTranslation('portalRecords');
  const { preference } = useDateTimeFormat();
  const query = useQuery({
    queryKey: ['portal', 'chart', 'medications'],
    queryFn: ({ signal }: { signal: AbortSignal }) => listMyMedications(signal),
  });
  return (
    <AsyncSection<readonly MedicationResponse[]>
      query={query}
      loadingLabel={t('medications.loading')}
      isEmpty={(data) => data.length === 0}
      emptyLabel={t('medications.empty')}
    >
      {(medications) => (
        <div className="flex flex-col gap-2">
          {medications.map((medication) => (
            <RecordCard key={medication.id}>
              <DetailRow label={t('medications.name')}>{medication.name}</DetailRow>
              <DetailRow label={t('medications.dose')}>
                {[medication.dose, medication.route, medication.frequency]
                  .filter((value): value is string => value !== null && value.length > 0)
                  .join(' · ') || '—'}
              </DetailRow>
              <DetailRow label={t('medications.status')}>
                <Badge>{medication.status}</Badge>
              </DetailRow>
              <DetailRow label={t('medications.started')}>
                {medication.startDate === null
                  ? '—'
                  : formatPreferredCalendarDate(medication.startDate, preference)}
              </DetailRow>
            </RecordCard>
          ))}
        </div>
      )}
    </AsyncSection>
  );
}

function ImmunizationsPanel() {
  const { t } = useTranslation('portalRecords');
  const { preference } = useDateTimeFormat();
  const query = useQuery({
    queryKey: ['portal', 'chart', 'immunizations'],
    queryFn: ({ signal }: { signal: AbortSignal }) => listMyImmunizations(signal),
  });
  return (
    <AsyncSection<readonly ImmunizationResponse[]>
      query={query}
      loadingLabel={t('immunizations.loading')}
      isEmpty={(data) => data.length === 0}
      emptyLabel={t('immunizations.empty')}
    >
      {(immunizations) => (
        <div className="flex flex-col gap-2">
          {immunizations.map((immunization) => (
            <RecordCard key={immunization.id}>
              <DetailRow label={t('immunizations.vaccine')}>{immunization.vaccine}</DetailRow>
              <DetailRow label={t('immunizations.administered')}>
                {formatPreferredCalendarDate(immunization.administeredDate, preference)}
              </DetailRow>
            </RecordCard>
          ))}
        </div>
      )}
    </AsyncSection>
  );
}

function VitalsPanel() {
  const { t } = useTranslation('portalRecords');
  const { preference } = useDateTimeFormat();
  const query = useQuery({
    queryKey: ['portal', 'chart', 'vitals'],
    queryFn: ({ signal }: { signal: AbortSignal }) => listMyVitals(signal),
  });
  return (
    <AsyncSection<readonly VitalResponse[]>
      query={query}
      loadingLabel={t('vitals.loading')}
      isEmpty={(data) => data.length === 0}
      emptyLabel={t('vitals.empty')}
    >
      {(vitals) => (
        <div className="flex flex-col gap-2">
          {vitals.map((vital) => (
            <RecordCard key={vital.id}>
              <DetailRow label={t('vitals.type')}>{vital.vitalType}</DetailRow>
              <DetailRow label={t('vitals.value')}>
                {vital.value} {vital.unit}
              </DetailRow>
              <DetailRow label={t('vitals.recorded')}>
                {formatDateAndTime(vital.recordedAt, preference)}
              </DetailRow>
            </RecordCard>
          ))}
        </div>
      )}
    </AsyncSection>
  );
}

function LabsPanel() {
  const { t } = useTranslation('portalRecords');
  const { preference } = useDateTimeFormat();
  const query = useQuery({
    queryKey: ['portal', 'chart', 'labs'],
    queryFn: ({ signal }: { signal: AbortSignal }) => listMyLabResults(signal),
  });
  return (
    <AsyncSection<readonly LabResultResponse[]>
      query={query}
      loadingLabel={t('labs.loading')}
      isEmpty={(data) => data.length === 0}
      emptyLabel={t('labs.empty')}
    >
      {(labResults) => (
        <div className="flex flex-col gap-2">
          {labResults.map((lab) => (
            <RecordCard key={lab.id}>
              <DetailRow label={t('labs.test')}>{lab.testName}</DetailRow>
              <DetailRow label={t('labs.result')}>
                {lab.value} {lab.unit}{' '}
                {lab.abnormalFlag !== 'normal' ? (
                  <Badge tone="warning">{lab.abnormalFlag}</Badge>
                ) : null}
              </DetailRow>
              <DetailRow label={t('labs.referenceRange')}>{lab.referenceRange}</DetailRow>
              <DetailRow label={t('labs.resulted')}>
                {formatDateAndTime(lab.resultedAt, preference)}
              </DetailRow>
            </RecordCard>
          ))}
        </div>
      )}
    </AsyncSection>
  );
}

function NotesPanel() {
  const { t } = useTranslation('portalRecords');
  const { preference } = useDateTimeFormat();
  const query = useQuery({
    queryKey: ['portal', 'chart', 'notes'],
    queryFn: ({ signal }: { signal: AbortSignal }) => listMySignedNotes(signal),
  });
  return (
    <AsyncSection<readonly NoteResponse[]>
      query={query}
      loadingLabel={t('notes.loading')}
      isEmpty={(data) => data.length === 0}
      emptyLabel={t('notes.empty')}
    >
      {(notes) => (
        <div className="flex flex-col gap-2">
          {notes.map((note) => (
            <RecordCard key={note.id}>
              <DetailRow label={t('notes.type')}>{note.noteType}</DetailRow>
              <DetailRow label={t('notes.signed')}>
                {note.signedAt === null ? '—' : formatDateAndTime(note.signedAt, preference)}
              </DetailRow>
              <DetailRow label={t('notes.content')}>
                <span className="whitespace-pre-wrap">{note.content}</span>
              </DetailRow>
            </RecordCard>
          ))}
        </div>
      )}
    </AsyncSection>
  );
}
